import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import ApiService from 'helpers/apiService';
import { getDefaultProjectId } from 'helpers/access';

const STORAGE_KEY = 'currentProjectId';

export const ProjectContext = createContext(null);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  // #4: инициализация из localStorage ИЛИ из JWT claim `pid` (проект по умолчанию,
  // сохранённый на бэке). Токен переживает простой/refresh → проект не сбрасывается.
  const [currentProjectId, setCurrentProjectIdState] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || getDefaultProjectId() || null;
  });
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const retriedRef = useRef(false); // #3: одноразовый retry при пустом списке

  // Синхронизируем localStorage с выбором из токена (нужно для X-Project-ID
  // header в axios-интерсепторе, который читает именно localStorage).
  useEffect(() => {
    if (currentProjectId && localStorage.getItem(STORAGE_KEY) !== currentProjectId) {
      localStorage.setItem(STORAGE_KEY, currentProjectId);
    }
  }, [currentProjectId]);

  const setCurrentProjectId = useCallback(id => {
    setCurrentProjectIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    // #4: persist на бэк (best-effort) — попадёт в JWT claim pid при следующем
    // login/refresh, чтобы выбор сохранялся между сессиями/устройствами.
    ApiService.setDefaultProject(id);
  }, []);

  const currentProject = projects.find(p => p.id === currentProjectId) || null;

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setProjects([]);
        return;
      }
      // v71+: /project/v1/my — открытый листинг без AccessProjectList.
      // Раньше через getProjects (/project/v1) — операторы без projectList
      // access получали 403, Logo фолбэкался на BRAND_NAME «Админ-панель»
      // вместо имени проекта.
      const response = await ApiService.getMyProjects();
      const rawList = response?.data ?? response ?? [];
      const list = Array.isArray(rawList) ? rawList : [];
      setProjects(list);
      const savedId = localStorage.getItem(STORAGE_KEY);
      // #4: дефолт-выбор предпочитает проект из токена (claim pid), затем list[0].
      const tokenPid = getDefaultProjectId();
      const pick = (list.find(p => p.id === tokenPid) ? tokenPid : list[0]?.id) ?? null;
      // v49 fix-1: НЕ сбрасывать saved project пока list пустой —
      // могут быть transient'ные 403/network errors, после которых fetchProjects
      // вернёт пустой массив и старая логика стирала savedId, сбрасывая UI
      // на дефолтный проект. Сбрасываем только когда:
      //   - savedId есть, list загружен и НЕ пустой, но savedId в нём отсутствует
      // (т.е. реально удалённый или другой проект).
      if (savedId && list.length > 0 && !list.find(p => p.id === savedId)) {
        localStorage.removeItem(STORAGE_KEY);
        setCurrentProjectIdState(pick);
        if (pick) localStorage.setItem(STORAGE_KEY, pick);
      } else if (!savedId && list.length > 0 && pick) {
        setCurrentProjectIdState(pick);
        localStorage.setItem(STORAGE_KEY, pick);
      }
      // Если list пустой — оставляем currentProjectIdState как есть (из localStorage/токена).

      // #3: список пуст, но токен есть → вероятна гонка login→fetch (первый
      // запрос ушёл до записи токена / transient 401). Один отложенный retry.
      if (list.length === 0 && localStorage.getItem('token') && !retriedRef.current) {
        retriedRef.current = true;
        setTimeout(() => { fetchProjects(); }, 700);
      }
    } catch (e) {
      console.warn('Projects fetch failed:', e);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    // Рефетч при появлении/смене токена в другой вкладке (NAV-2 паттерн).
    const onStorage = e => {
      if (e.key === 'token' && e.newValue) {
        retriedRef.current = false;
        fetchProjects();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [fetchProjects]);

  const value = {
    currentProjectId,
    currentProject,
    projects,
    setCurrentProjectId,
    fetchProjects,
    loading
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

ProjectProvider.propTypes = {
  children: PropTypes.node.isRequired
};
